import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ChatArea } from 'src/components/ChatArea';
import { useAppDispatch, useAppSelector } from 'src/redux/redux-store/hooks';
import {
  BackgroundImage,
  Box,
  Skeleton,
  Select,
  Tabs,
  Text,
  UnstyledButton,
  createStyles,
  useMantineTheme,
  Tooltip,
} from '@mantine/core';
import Icon from 'src/components/common/Icons/Icons/Icon';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import useParentChat from 'src/hooks/useParentChat';
import { StyledContainer } from 'src/components/common/StyledComponents/Container';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  handleUpdateAppSettings,
  handleUserUpdate,
  handleWalkthroughUpdate,
} from 'src/redux/redux-store';
import {
  ChatStyledContainerInset,
  StyledContainerInset,
} from 'src/components/common/StyledComponents/BackgroundContainer';
import { Notifications } from '@mantine/notifications';
import ChatInput from 'src/components/common/Inputs/ChatMessageInput';
import { useChat } from 'src/hooks/useChat';
import * as chatActions from 'src/redux/redux-store/chat/chatReducer';
import {
  NotificationTypes,
  PubSubNotificationTypes,
} from 'src/enums/Notifications';
import useResponsive from 'src/hooks/useResponsive';
import { sendFailureNotification } from 'src/utils/notifications';
import { ChatSession } from 'src/interfaces/Chat';
import DynamicLayout from 'src/components/Layout/DynamicLayout';
import ParentChatSidebar from 'src/components/Sidebars/Parent/ParentSIdebarHome';
import { Disclaimer } from 'src/components/Chat/Disclaimer';
import { useFileUpload } from 'src/hooks/useFileUpload';
import { CustomButton } from 'src/components/common/Buttons/CustomButton';
import { usePayments } from 'src/hooks/usePayment';
import ReactJoyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  ORIGIN,
  Status,
  STATUS,
  Step,
  StepMerged,
  StepProps,
} from 'react-joyride';
import { ParentWalkthrough } from 'src/components/Walkthroughs/parent';
import { parentRoutes } from 'src/navigators/routes/ParentRoutes';
import TrialExpiryNoticeModal from 'src/components/Modals/TrialExpiryNotice';
import IOSSafeChatContainer from 'src/components/Layout/IOSSafeChatContainer';
import SubjectDropdown from 'src/components/Chat/SubjectDropdown';

const useStyles = createStyles((theme) => ({
  parentContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyItems: 'center',
  },
  textAreaAndDisclaimer: {
    alignSelf: 'center',
    justifySelf: 'center',

    '@media (min-width: 768px)': {},
  },
}));

const ParentDashboard = React.memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const user = useAppSelector((state) => state.user);
  const appSettings = useAppSelector((state) => state.app).appSettings;
  const sessionId = useAppSelector((state) => state.chat.selectedSession);
  const [trialExpiryNoticeOpen, setTrialExpiryNoticeOpen] = useState(false);
  const { backgroundColor, backgroundColorExterior, isMobile, isDesktop } =
    useResponsive();

  // Use the parent chat hook for level and subject management
  const {
    levels,
    subjects: parentSubjects,
    loading: subjectsLoading,
    handleSubjectChange,
    createNewSession,
    activeSubject,
    initializeChat,
  } = useParentChat();

  const sessionsLoading = useAppSelector((state) => state.chat.sessionsLoading);
  const {
    uploadStatus,
    uploadedFile,
    setUploadedFile,
    uploadFile,
    client,
    setUploadStatus,
  } = useFileUpload();
  // Use the chat hook without arguments
  const chat = useChat();
  // Destructure the chat object to get the needed functions and state
  const {
    inputMessage,
    setInputMessage,
    sendMessage,
    getMessages,
    messages,
    streaming,
    triggerMessageForFileUpload,
    triggerMessageForFileUploadError,
    subjects,
  } = chat;
  const { userPlan, getCurrentPlan } = usePayments();
  const [key, setKey] = useState(0);
  const [initialMessagesLoaded, setInitialMessagesLoaded] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // Reset initial load state when subject changes
  useEffect(() => {
    setInitialMessagesLoaded(false);
  }, [activeSubject]);

  // Handle message loading when selected session changes
  useEffect(() => {
    const loadMessages = async () => {
      if (!sessionId?.id) return;

      try {
        console.log('Loading messages for session:', sessionId.id);
        await getMessages(sessionId.id);
      } catch (error) {
        console.error('Error loading messages:', error);
        sendFailureNotification(
          NotificationTypes.chatSession,
          'Failed to load messages.'
        );
      }
    };

    loadMessages();
  }, [sessionId?.id]);

  useEffect(() => {
    initializeChat();
  }, []);

  // Track when all initial data is loaded
  useEffect(() => {
    if (!subjectsLoading && !sessionsLoading && initialMessagesLoaded) {
      setPageLoading(false);
    } else {
      setPageLoading(true);
    }
    setTimeout(() => {
      setPageLoading(false);
    }, 5000);
  }, [subjectsLoading, sessionsLoading, initialMessagesLoaded]);

  const handleSend = async (message: string) => {
    let idSession = sessionId;
    let read_url: string | undefined;
    if (uploadedFile) {
      try {
        triggerMessageForFileUpload(message, uploadedFile);
        const res = await uploadFile(uploadedFile, idSession.id);
        if (!res) {
          throw new Error('File upload failed');
        }
        read_url = res;
        // File upload successful
      } catch (error) {
        console.error('Error during file upload:', error);
        triggerMessageForFileUploadError(message);
        setUploadStatus('idle');
        setUploadedFile(null);
        return; // Exit the function if file upload failed
      }
    }

    // Proceed with sending the message
    try {
      await sendMessage(message, read_url);
      setUploadedFile(null);
      setInputMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      sendFailureNotification(
        NotificationTypes.chatSession,
        'Failed to send message. Please try again.'
      );
    } finally {
      setUploadStatus('idle');
    }
  };

  useEffect(() => {
    if (client) {
      const handleServerMessage = (data: any) => {
        if (data.message.data) {
          const message = JSON.parse(data.message.data.toString());
          if (message.event_type === PubSubNotificationTypes.appInfoUpdate) {
            dispatch(handleUpdateAppSettings());
          }
        }
      };

      client.on('server-message', handleServerMessage);
      return () => {
        client.off('server-message', handleServerMessage);
      };
    }
  }, [client, dispatch]);

  useEffect(() => {
    if (user && userPlan) {
      if (!user.trial_expired && !userPlan?.has_active_subscription) {
        setTrialExpiryNoticeOpen(
          user.trial_days ? user.trial_days <= 3 : false
        );
      }
    }
  }, [userPlan, user]);

  const handleNewSession = useCallback(async () => {
    try {
      await createNewSession('New Session');
    } catch (error) {
      console.error('Error creating new session:', error);
      sendFailureNotification(
        NotificationTypes.chatSession,
        'Failed to create a new chat session. Please try again.'
      );
    }
  }, [createNewSession]);

  console.log('I AM IN Dashboard 🙌 ');

  const header = (
    <div className="flex flex-row w-full justify-between">
      <div className="flex flex-row items-center gap-2">
        <Icon name="chat" className="size-6 md:size-8" />
        <Text>{sessionId.title}</Text>
      </div>
      <div className="flex flex-row items-center gap-2">
        {/* Subjects Skeleton */}
        {false && (pageLoading || !activeSubject) ? (
          <div className="sticky top-0 z-10 -mt-2">
            <div className="px-4 flex gap-3 pb-[1px] justify-center">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 py-3 px-4 rounded-b-xl bg-primary/20"
                >
                  {/* <Skeleton height={20} width={20} radius='sm' /> */}
                  <Skeleton height={20} width={80} radius="sm" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="sticky top-0 z-10 py-2">
            <SubjectDropdown
              subjects={parentSubjects.map((s) => s.name)}
              activeSubject={
                activeSubject ||
                (parentSubjects?.length > 0 && parentSubjects[0]?.name) ||
                ''
              }
              onSubjectChange={handleSubjectChange}
            />
          </div>
        )}
        <Tooltip label="New Chat">
          <div
            onClick={handleNewSession}
            className="new-chat-step bg-primary text-white text-base font-semibold rounded-lg full py-1 px-4 hover cursor-pointer flex items-center gap-x-1"
          >
            <Icon
              name="edit"
              color="white"
              size="24px"
              className="-mt-[2px] -ml-[1.5px]"
            />
            New Chat
          </div>
        </Tooltip>
      </div>
    </div>
  );

  // const handleSubjectChange = useCallback((subjectName: string) => {
  //   setActiveSubject(subjectName);
  //   fetchSessionsForSubject(subjectName);
  // }, [fetchSessionsForSubject]);

  useEffect(() => {
    if (user.walkthrough_completed) {
      setKey((prevKey) => prevKey + 1);
    }
  }, [user.walkthrough_completed]);

  return (
    <DynamicLayout
      key={key}
      heading={header}
      rightContent={
        <>
          <SubjectDropdown
            subjects={parentSubjects.map((s) => s.name)}
            activeSubject={
              activeSubject ||
              (parentSubjects?.length > 0 && parentSubjects[0]?.name) ||
              ''
            }
            onSubjectChange={handleSubjectChange}
          />
          <UnstyledButton
            className={`w-6 h-6 rounded-md p-[5px] ${
              levels.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-secondary'
            }`}
            onClick={levels.length === 0 ? undefined : handleNewSession}
            disabled={levels.length === 0}
          >
            <img
              src="/icons/newChat.png"
              alt="edit"
              className={levels.length === 0 ? 'opacity-50' : ''}
            />
          </UnstyledButton>
        </>
      }
      SidebarComponent={ParentChatSidebar}
    >
      <ParentWalkthrough
        startWalkthrough={!user.walkthrough_completed && !isMobile}
        onCloseWalkthrough={() => {
          dispatch(handleWalkthroughUpdate());
        }}
      />

      <StyledContainer
        theme={{ backgroundColor: backgroundColorExterior }}
        className="flex flex-col h-[calc(100%-55px)] md:h-[calc(100%-70px)]"
      >
        <ChatStyledContainerInset
          theme={{ backgroundColor: backgroundColor }}
          className="flex-1 flex flex-col min-h-0"
        >
          <TrialExpiryNoticeModal
            isOpen={trialExpiryNoticeOpen}
            onClose={() => setTrialExpiryNoticeOpen(false)}
            onUpgrade={() => navigate(parentRoutes.PAYMENT)}
          />
          <BackgroundImage
            src="/images/doodles.png"
            className="flex-1 flex flex-col min-h-0"
          >
            <Notifications
              position={isMobile ? 'top-center' : 'bottom-right'}
              limit={isMobile ? 1 : 5}
            />
            <div
              className={`flex-1 flex flex-col min-h-0 h-full w-full lg:w-[700px] xl:w-[85%] py-0 md:pb-2 mx-auto`}
            >
              {/* Chat Area Skeleton */}
              <div className="flex-1 relative min-h-0">
                {pageLoading ||
                (messages.length === 0 &&
                  !activeSubject &&
                  levels.length > 0) ? (
                  <div className="h-full overflow-y-auto py-8 px-2 space-y-8">
                    {/* User message skeleton */}
                    <div className="flex justify-end gap-3">
                      <div className="max-w-[80%] md:max-w-[70%] lg:max-w-[60%] xl:max-w-[50%] w-full">
                        <div className="bg-primary/10 p-4 rounded-t-2xl rounded-l-2xl">
                          <Skeleton height={16} radius="md" width="80%" />
                          <Skeleton
                            height={16}
                            radius="md"
                            width="60%"
                            mt={8}
                          />
                        </div>
                      </div>
                      <Skeleton height={40} width={40} circle />
                    </div>

                    {/* AI message skeleton */}
                    <div className="flex flex-col">
                      <div className="flex gap-3 max-w-[90%] md:max-w-[80%] lg:max-w-[70%] xl:max-w-[60%]">
                        <Skeleton height={40} width={40} circle />
                        <div className="flex-1">
                          <div className="bg-gray-100 p-4 rounded-r-2xl rounded-bl-2xl">
                            <Skeleton height={16} radius="md" width="90%" />
                            <Skeleton
                              height={16}
                              radius="md"
                              width="70%"
                              mt={8}
                            />
                            <Skeleton
                              height={16}
                              radius="md"
                              width="60%"
                              mt={8}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Loading indicator */}
                    <div className="flex justify-center py-4">
                      <div className="flex items-center gap-2">
                        <Skeleton height={12} width={12} circle />
                        <Skeleton height={12} width={12} circle />
                        <Skeleton height={12} width={12} circle />
                      </div>
                    </div>
                  </div>
                ) : levels.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-4 p-4 h-full">
                    <Text size="lg" weight={500}>
                      Create children first to chat
                    </Text>
                    {/* <Text size='sm' weight={500} color='gray'>
                      (Refresh if you already did)
                    </Text> */}
                    <CustomButton
                      onClick={() => navigate(parentRoutes.REGISTER_CHILD)}
                      size="sm"
                      className="w-32"
                    >
                      Create Child
                    </CustomButton>
                  </div>
                ) : (
                  <ChatArea
                    sendMessage={sendMessage}
                    username={user.name}
                    messages={messages}
                    streaming={streaming}
                    subjects={subjects}
                  />
                )}
              </div>
              {/* Fixed input area */}
              {sessionsLoading || subjectsLoading ? (
                <div className="flex gap-2 items-center">
                  <Skeleton height={44} radius="xl" className="flex-1" />
                  <Skeleton height={44} width={44} radius="xl" className="p-2">
                    <Skeleton height={24} width={24} radius="md" />
                  </Skeleton>
                  <Skeleton height={44} width={44} radius="xl" className="p-2">
                    <Skeleton height={24} width={24} radius="md" />
                  </Skeleton>
                </div>
              ) : levels.length === 0 ? null : ( // </div> //   </CustomButton> //     Create Child //   > //     className='w-32' //     size='sm' //     onClick={() => navigate(parentRoutes.REGISTER_CHILD)} //   <CustomButton //   </Text> //     Create children first to chat //   <Text size='lg' weight={500}> // <div className='flex flex-col items-center justify-center gap-4 p-4'>
                <>
                  <ChatInput
                    handleUpgrade={() => navigate(parentRoutes.PAYMENT)}
                    access={
                      userPlan?.has_active_subscription
                        ? 'Premium'
                        : user.trial_expired
                        ? 'Free'
                        : 'Premium'
                    }
                    inputMessage={inputMessage}
                    setInputMessage={setInputMessage}
                    onSend={handleSend}
                    uploadedFile={uploadedFile}
                    setUploadedFile={setUploadedFile}
                    uploadStatus={uploadStatus}
                    streaming={streaming}
                    disabled={streaming || !inputMessage}
                  />
                  <Disclaimer text={appSettings?.parent_disclaimers} />
                </>
              )}
              {/* {sessionsLoading ? (
                <div className='flex gap-2 items-center'>
                  <Skeleton height={44} radius='xl' className='flex-1' />
                  <Skeleton height={44} width={44} radius='xl' className='p-2'>
                    <Skeleton height={24} width={24} radius='md' />
                  </Skeleton>
                  <Skeleton height={44} width={44} radius='xl' className='p-2'>
                    <Skeleton height={24} width={24} radius='md' />
                  </Skeleton>
                </div>
              ) : levels.length === 0 ? //   <Text size='lg' weight={500}> // <div className='flex flex-col items-center justify-center gap-4 p-4'>
              //     Create children first to chat
              //   </Text>
              //   <CustomButton
              //     onClick={() => navigate(parentRoutes.REGISTER_CHILD)}
              //     size='sm'
              //     className='w-32'
              //   >
              //     Create Child
              //   </CustomButton>
              // </div>
              null : (
                <>
                  <ChatInput
                    handleUpgrade={() => navigate(parentRoutes.PAYMENT)}
                    access={
                      userPlan?.has_active_subscription
                        ? "Premium"
                        : user.trial_expired
                        ? "Free"
                        : "Premium"
                    }
                    inputMessage={inputMessage}
                    setInputMessage={setInputMessage}
                    onSend={handleSend}
                    uploadedFile={uploadedFile}
                    setUploadedFile={setUploadedFile}
                    uploadStatus={uploadStatus}
                    streaming={streaming}
                    disabled={streaming || !inputMessage}
                  />
                  <Disclaimer text={appSettings?.parent_disclaimers} />
                </>
              )} */}
              {/* </div> */}
            </div>
          </BackgroundImage>
        </ChatStyledContainerInset>
      </StyledContainer>
    </DynamicLayout>
  );
});

export default ParentDashboard;
